import { Component, effect, input, OnInit } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { EChartsCoreOption } from 'echarts/core';
import { NgxEchartsDirective } from 'ngx-echarts';
import { Project } from 'src/app/entities/project/project';
import { TimeTrack } from 'src/app/entities/time-track/time-track';
import { TooltipFormatterParams } from 'src/app/models/echarts/tooltip.model';
import { DateService } from 'src/app/services/date-service/date.service';
import { FiltersService } from 'src/app/services/filters-service/filters.service';

/**
 * Available date range filters for the graph visualization.
 * - Today: Shows data for the current day
 * - Yesterday: Shows data for the previous day
 * - Week: Shows data for the current week (Monday to Sunday)
 * - Month: Shows data for the current month
 * - Custom: Allows selecting a custom date range
 */
enum GraphDatesFilter {
  Today,
  Yesterday,
  Week,
  Month,
  Custom
}


/**
 * Configuration options for customizing the chart's appearance and behavior
 */
interface CustomOptions {
  /**
   * Label to display in the chart legend and tooltips for this data series
   */
  seriesTitle?: string;

  /**
   * When true, displays a legend showing all data series names
   */
  displayLegend?: boolean;

  /**
   * When true, shows detailed information when hovering over data points
   */
  displayTooltip?: boolean;
}


/**
 * Represents a single data point in the time tracking chart
 */
export interface ChartData {
  /** The date when the time was tracked */
  date: Date;
  /** Total time tracked in seconds for this date */
  value: number;
}


@Component({
  selector: 'app-summary-graphs',
  imports: [NgxEchartsDirective, ReactiveFormsModule],
  templateUrl: './summary-graphs.component.html',
  styleUrl: './summary-graphs.component.scss'
})
export class SummaryGraphsComponent implements OnInit {
  projects = input<Project[]>([]);
  timeTracks = input<TimeTrack[]>([]);

  periodStart = input.required<Date>();

  periodEnd = input.required<Date>();


  graphBy = GraphDatesFilter.Week;

  /**
   * ECharts configuration object that defines how the chart is rendered
   * Updated whenever the data or display options change
   */
  options: EChartsCoreOption = {};

  /**
   * Reference date used for navigation and period calculations
   * Defaults to today but can be changed when navigating through periods
   */
  currentDate = new Date();


  /**
   * Effect to update chart options when data changes
   */
  protected echartsOptionsEffect = effect(() => {
    this.setEchartOptions(this.getTracksByDays(), undefined)
  });

  // =========================================================================
  // Lifecycle Hooks
  // =========================================================================

  constructor(
    protected dateService: DateService,
    protected filters: FiltersService,
    protected router: Router
  ) { }

  ngOnInit(): void {
    this.setEchartOptions(this.getTracksByDays(), undefined);
  }

  // =========================================================================
  // Protected Methods
  // =========================================================================


  protected setTracksFilterDates(start: string, end: string): void {
    this.filters.setTimeTracksFilters({
      from: start,
      to: end
    });
  }

  /**
   * Determines if there are any time tracks within the current period.
   * 
   * @returns {boolean} true if at least one time track exists within the current
   * period's date range (inclusive of start and end dates), false otherwise.
   */
  protected hasData(): boolean {
    return this.timeTracks().some(track => {
      const periodStart = this.periodStart();
      const periodEnd = this.periodEnd();
      return track.from >= periodStart && track.from <= periodEnd && (track.time_spent !== undefined);
    });
  }

  /**
   * Aggregates all time tracking data by days within the current period.
   * 
   * Process:
   * 1. Creates a map with all days in the period initialized to 0 hours
   * 2. Aggregates time spent for each track that falls within the period
   * 3. Converts the map to an array of ChartData objects
   * 
   * @returns An array of ChartData objects containing:
   *          - date: The day for which time was tracked
   *          - value: Total seconds tracked for that day
   *          Returns empty array if no data exists for the period
   */
  protected getTracksByDays(): ChartData[] {
    const tracksByDay = new Map<string, number>();
    const periodStart = this.periodStart();
    const periodEnd = this.periodEnd();

    if (!this.hasData()) return [];

    for (let d = new Date(periodStart); d <= periodEnd; d.setDate(d.getDate() + 1)) {
      tracksByDay.set(new Date(d).toLocaleDateString(), 0);
    }

    // Add time tracks that fall within the week
    this.timeTracks().forEach(track => {
      const trackDate = track.from;
      if (trackDate >= periodStart && trackDate <= periodEnd) {
        const currentDate = tracksByDay.get(trackDate.toLocaleDateString());
        if (currentDate !== undefined && track.time_spent !== undefined) {
          // Ensure we're adding the time spent even if it's very small
          tracksByDay.set(trackDate.toLocaleDateString(), currentDate + track.time_spent);
        }
      }
    });

    const items: ChartData[] = [];
    for (const [key, value] of tracksByDay.entries()) {
      items.push({
        date: new Date(key),
        value: value
      });
    }

    return items;
  }

  /**
   * Configures and updates the ECharts visualization options with new data.
   * 
   * Configuration includes:
   * - Legend: Shows/hides data series labels with left alignment
   * - Tooltip: Displays date and hours on hover with custom formatting
   * - X-axis: Time-based axis with automatic date formatting
   * - Y-axis: Numeric axis for hours (automatically scaled)
   * - Series: Animated bar chart with customizable colors and labels
   * - Zoom: Interactive slider for navigating longer periods
   * - Animations: Smooth transitions with elastic easing
   * 
   * @param data - Array of ChartData objects containing date and value (seconds) pairs
   * @param options - Optional configuration overrides:
   *   - seriesTitle: Custom label for the data series
   *   - displayLegend: Show/hide the chart legend
   *   - displayTooltip: Enable/disable hover tooltips
   */
  private setEchartOptions(data: ChartData[], options: CustomOptions | undefined): void {
    // Transform seconds to hours for display
    const hoursData = data.map(item => ({
      date: item.date,
      value: parseFloat((item.value / 3600).toFixed(2))  // Convert seconds to hours with 2 decimal places
    }));

    this.options = {
      legend: false,
      tooltip: {
        trigger: 'item',
        formatter: (params: TooltipFormatterParams<ChartData>) => {
          return (`${params.seriesName} · ${params.data.date.toLocaleDateString()} <br> ${params.data.value} h`)
        },
        className: 'echarts-tooltip'
      },
      dataset: {
        source: hoursData,
      },
      xAxis: {
        type: 'time',
        silent: false,
        splitLine: {
          show: false,
        },
      },
      yAxis: {
        name: 'Hours',
        minInterval: 0.01, // Set minimum interval to 0.01 hours (36 seconds)
        axisLabel: {
          formatter: '{value} h'
        }
      },
      series: [
        {
          name: options?.seriesTitle,
          type: 'bar',
          animationDelay: (idx: number) => idx * 10,
        },
      ],
      dataZoom: [
        {
          type: 'slider',
          xAxisIndex: 0,
          filterMode: 'empty',
          startValue: data[0]?.date,
          endValue: data[data.length - 1]?.date
        }
      ],
      animationEasing: 'elasticOut',
      animationDelayUpdate: idx => idx * 5,
    };
  }
}
